// GlossaryPopover — единственный (глобальный) поповер слоя глоссария.
//
// Смонтирован один раз в `__root.tsx`. Слушает документ делегированно:
//   • desktop — `mouseover` / `mouseout` по `[data-glossary]`;
//   • touch   — `click` (hover не существует), закрытие по `pointerdown` вне.
//
// Внутри TipTap (`.ProseMirror`) не вмешиваемся: у редактора конспекта свой
// поповер (notes-editor.tsx GlossaryHoverPortal) — иначе на конспекте всплывали
// бы два одинаковых окна.
//
// Определение рендерится через RichContent: в определениях бывает LaTeX, а
// сырой <p> его не покажет.

import { Button, CitationChip, Popover, PopoverAnchor, PopoverContent } from "@studyhub/ui";
import { IconTooltip } from "@studyhub/ui/components/tooltip";
import { toast } from "@studyhub/ui/toast";
import { IconCards, IconCheck } from "@tabler/icons-react";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RichContent } from "@/components/shared/rich-content";
import { type GlossaryScope, useGlossaryTermLookup } from "@/hooks/use-glossary-terms";
import type { GlossaryTermLite } from "@/lib/glossary/build-index";
import { track } from "@/lib/observability/analytics";
import { IS_MOBILE_PLATFORM } from "@/lib/platform-detect";
import { usePowerSyncQuery } from "@/lib/powersync/use-query";
import { api } from "@/lib/trpc-client";

/** Поверхности из каталога аналитики (`glossary_term_hovered.surface`). */
type GlossarySurface =
	| "note"
	| "audio-transcript"
	| "recording-transcript"
	| "tutor-chat"
	| "voice-transcript"
	| "description";

const SURFACES: readonly GlossarySurface[] = [
	"note",
	"audio-transcript",
	"recording-transcript",
	"tutor-chat",
	"voice-transcript",
	"description",
];

const MARK_SELECTOR = "[data-glossary='true']";
/** Задержка закрытия — успеть довести курсор с метки до поповера. */
const CLOSE_DELAY_MS = 140;
const DEFINITION_CLAMP_CHARS = 160;

interface ActiveMark {
	rect: DOMRect;
	term: string;
	termId: string | null;
	surface: GlossarySurface;
	scope: GlossaryScope;
}

function readSurface(el: HTMLElement): GlossarySurface {
	const raw = el.closest<HTMLElement>("[data-glossary-surface]")?.dataset.glossarySurface;
	return SURFACES.find((s) => s === raw) ?? "description";
}

function readScope(el: HTMLElement): GlossaryScope {
	const host = el.closest<HTMLElement>("[data-glossary-plan],[data-glossary-subsection]");
	return {
		planId: host?.dataset.glossaryPlan || undefined,
		subsectionId: host?.dataset.glossarySubsection || undefined,
	};
}

export function GlossaryPopover(): React.ReactElement | null {
	const [active, setActive] = useState<ActiveMark | null>(null);
	const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
	const anchorElRef = useRef<HTMLElement | null>(null);

	const cancelClose = useCallback(() => {
		if (closeTimer.current) clearTimeout(closeTimer.current);
		closeTimer.current = undefined;
	}, []);

	const scheduleClose = useCallback(() => {
		cancelClose();
		closeTimer.current = setTimeout(() => {
			anchorElRef.current = null;
			setActive(null);
		}, CLOSE_DELAY_MS);
	}, [cancelClose]);

	const openFor = useCallback((el: HTMLElement) => {
		const term = el.getAttribute("data-term");
		if (!term) return;
		anchorElRef.current = el;
		setActive({
			rect: el.getBoundingClientRect(),
			term,
			termId: el.getAttribute("data-term-id"),
			surface: readSurface(el),
			scope: readScope(el),
		});
	}, []);

	useEffect(() => () => cancelClose(), [cancelClose]);

	// Делегированные обработчики. `.ProseMirror` отдаём редактору конспекта.
	useEffect(() => {
		function markFrom(event: Event): HTMLElement | null {
			// target бывает не Element (document, текстовый узел, window) — у него нет closest.
			if (!(event.target instanceof Element)) return null;
			const target = event.target.closest<HTMLElement>(MARK_SELECTOR);
			if (!target) return null;
			if (target.closest(".ProseMirror")) return null;
			return target;
		}

		function onMouseOver(event: Event) {
			if (IS_MOBILE_PLATFORM) return;
			const mark = markFrom(event);
			if (!mark) return;
			cancelClose();
			if (anchorElRef.current === mark) return;
			openFor(mark);
		}

		function onMouseOut(event: Event) {
			if (IS_MOBILE_PLATFORM) return;
			if (!anchorElRef.current) return;
			const related = (event as MouseEvent).relatedTarget as HTMLElement | null;
			if (related?.closest?.(MARK_SELECTOR)) return;
			if (related?.closest?.("[data-glossary-popover]")) return;
			scheduleClose();
		}

		function onClick(event: Event) {
			if (!IS_MOBILE_PLATFORM) return;
			const mark = markFrom(event);
			if (!mark) return;
			cancelClose();
			if (anchorElRef.current === mark) {
				anchorElRef.current = null;
				setActive(null);
				return;
			}
			openFor(mark);
		}

		function onPointerDown(event: Event) {
			if (!anchorElRef.current) return;
			const target = event.target as HTMLElement | null;
			if (target?.closest?.(MARK_SELECTOR)) return;
			if (target?.closest?.("[data-glossary-popover]")) return;
			cancelClose();
			anchorElRef.current = null;
			setActive(null);
		}

		document.addEventListener("mouseover", onMouseOver);
		document.addEventListener("mouseout", onMouseOut);
		document.addEventListener("click", onClick);
		document.addEventListener("pointerdown", onPointerDown);
		return () => {
			document.removeEventListener("mouseover", onMouseOver);
			document.removeEventListener("mouseout", onMouseOut);
			document.removeEventListener("click", onClick);
			document.removeEventListener("pointerdown", onPointerDown);
		};
	}, [cancelClose, scheduleClose, openFor]);

	if (!active) return null;

	return (
		<Popover open>
			{/* Виртуальный якорь: поповер глобальный, а метка живёт в любом месте
			    страницы — позиционируемся по её rect. */}
			<PopoverAnchor asChild>
				<span
					aria-hidden
					style={{
						position: "fixed",
						left: active.rect.left,
						top: active.rect.top,
						width: active.rect.width,
						height: active.rect.height,
						pointerEvents: "none",
					}}
				/>
			</PopoverAnchor>
			<PopoverContent
				data-glossary-popover=""
				side="top"
				align="start"
				sideOffset={6}
				collisionPadding={12}
				// Фон НЕ задаём: PopoverContent наследует --sh-popover-bg.
				className="w-[min(320px,calc(100vw-2rem))] space-y-2 p-3 text-sm"
				onOpenAutoFocus={(e) => e.preventDefault()}
				onMouseEnter={cancelClose}
				onMouseLeave={() => {
					if (!IS_MOBILE_PLATFORM) scheduleClose();
				}}
			>
				<GlossaryPopoverBody active={active} />
			</PopoverContent>
		</Popover>
	);
}

function GlossaryPopoverBody({ active }: { active: ActiveMark }): React.ReactElement {
	const { t } = useTranslation("glossary");
	const lookup = useGlossaryTermLookup();
	const local = lookup({ term: active.term, id: active.termId });

	// Термин мог не попасть в локальный словарь (обрезан деградацией индекса или
	// ещё не синкнулся) — добираем с сервера СО СКОУПОМ: без него определение
	// темы проиграет случайному более свежему термину (§8.3).
	const remote = api.glossary.lookupBatch.useQuery(
		{
			terms: [active.term],
			...(active.scope.subsectionId ? { subsectionId: active.scope.subsectionId } : {}),
			...(active.scope.planId ? { planId: active.scope.planId } : {}),
		},
		{ enabled: !local, staleTime: 5 * 60_000, retry: false },
	);

	const match = remote.data?.[0]?.match ?? null;
	const term: GlossaryTermLite | null =
		local ??
		(match
			? {
					id: match.id,
					term: match.term,
					definition: match.definition,
					importance: match.importance,
					status: match.status,
					materialId: match.materialId,
					subsectionId: match.subsectionId,
					citation: match.citations[0]
						? {
								page: match.citations[0].page,
								timestamp: match.citations[0].timestamp,
								snippet: match.citations[0].snippet,
							}
						: null,
				}
			: null);

	// biome-ignore lint/correctness/useExhaustiveDependencies: событие — на открытие метки, поверхность стабильна внутри одного открытия
	useEffect(() => {
		track("glossary_term_hovered", { surface: active.surface });
	}, [active.term, active.surface]);

	if (!term) {
		return (
			<div className="space-y-1.5">
				<span className="font-semibold tracking-tight">{active.term}</span>
				<p className="text-xs text-muted-foreground">
					{remote.isLoading ? t("generation.inProgress") : t("empty")}
				</p>
			</div>
		);
	}

	return <GlossaryPopoverCard term={term} />;
}

/**
 * Тело поповера термина — ОДНО на все поверхности слоя.
 *
 * Экспортируется, потому что TipTap-поверхности (конспект темы, редактор
 * `/notes/$id`) рисуют свой поповер: Radix здесь не годится, триггер — узел
 * ProseMirror, а не React-элемент. Но СОДЕРЖИМОЕ обязано быть тем же, иначе в
 * чате у термина есть «в карточки» и источник, а в конспекте — голое
 * определение (жалоба 2026-08-09 про «единый интерфейс»).
 */
export function GlossaryPopoverCard({ term }: { term: GlossaryTermLite }): React.ReactElement {
	const { t } = useTranslation("glossary");
	const navigate = useNavigate();
	const [expanded, setExpanded] = useState(false);
	// «Уже добавлено» — по факту успешной мутации в этой сессии. Хранить это в
	// БД отдельным полем не за чем: повторный экспорт идемпотентен на сервере,
	// а человеку важно видеть, что нажатие сработало.
	const [added, setAdded] = useState(false);

	const materialRows = usePowerSyncQuery<{ title: string }>(
		`SELECT title FROM materials WHERE id = ? LIMIT 1`,
		[term.materialId ?? ""],
	);
	const materialTitle = materialRows.data?.[0]?.title;

	const exportMutation = api.glossary.exportToFlashcards.useMutation({
		onSuccess: (res) => {
			setAdded(true);
			toast.success(t("exportDone", { count: res.createdCount }));
		},
		onError: () => toast.error(t("exportFailed")),
	});

	const clamped = !expanded && term.definition.length > DEFINITION_CLAMP_CHARS;

	return (
		<>
			{/*
				`-my-1` + `items-center`: кнопка-иконка (28px) выше строки заголовка
				(20px), и при `items-start` под термином оставалось 8px пустоты, которые
				складывались с `space-y-2` контейнера — определение отъезжало почти на
				полторы строки вниз. Отрицательный margin съедает выступ кнопки, зазор
				до определения остаётся ровно один шаг сетки.
			*/}
			<div className="-my-1 flex items-center justify-between gap-2">
				<span className="min-w-0 flex-1 font-semibold leading-snug tracking-tight">
					{term.term}
				</span>
				{/*
					Знак — КАРТОЧКИ, а не звезда: кнопка добавляет термин в колоду
					повторения, а звезда читается как «в избранное» и вводила в
					заблуждение (жалоба 2026-08-09). После добавления кнопка становится
					заполненной и неактивной — видно, что действие уже сделано.
				*/}
				<IconTooltip label={added ? t("term.addedToFlashcards") : t("term.addToFlashcards")}>
					<Button
						size="icon"
						variant={added ? "secondary" : "ghost"}
						className="size-7 shrink-0 cursor-pointer"
						aria-label={added ? t("term.addedToFlashcards") : t("term.addToFlashcards")}
						disabled={exportMutation.isPending || added}
						onClick={() =>
							exportMutation.mutate({ termIds: [term.id], setName: t("exportSetName") })
						}
					>
						{added ? (
							<IconCheck size={14} stroke={2} className="text-success" />
						) : (
							<IconCards size={14} stroke={1.75} />
						)}
					</Button>
				</IconTooltip>
			</div>

			<RichContent
				content={term.definition}
				className={
					clamped
						? "line-clamp-3 text-muted-foreground leading-relaxed"
						: "text-muted-foreground leading-relaxed"
				}
			/>
			{clamped && (
				<button
					type="button"
					className="cursor-pointer text-xs font-medium text-primary pointer-coarse:min-h-11"
					onClick={() => setExpanded(true)}
				>
					{t("popover.more")}
				</button>
			)}

			{term.materialId && materialTitle && (
				<CitationChip
					source={{
						kind: "material",
						label: materialTitle,
						...(term.citation?.page ? { page: term.citation.page } : {}),
					}}
					className="cursor-pointer"
					onClick={() =>
						void navigate({
							to: "/materials/$materialId",
							params: { materialId: term.materialId ?? "" },
							search: { tab: "glossary" },
						})
					}
				/>
			)}
		</>
	);
}
